import { getLocalizedString, invariant, logError } from "@olenbetong/appframe-core";

import {
	DataHandler,
	type DataHandlerDescriptor,
	DataProviderHandler,
	type PagedRetrieveParameters,
	type PagedRetrieveResponse,
	type RequestError,
	type RetrieveParameters,
	type RetrieveResponse,
} from "./DataHandler.js";
import * as errors from "./errors.js";
import { DataObjectEvents, type DataObjectEventToValueMap, EventTarget } from "./events.js";
import { FileUploader } from "./FileUploader.js";
import type { FieldDefinition } from "./fields.js";
import { Paging } from "./Paging.js";
import { RecordSource } from "./RecordSource.js";
import { MemoryStorage, type Storage, type StorageArray, type StorageRecord, uid } from "./Storage.js";
import { UploadManager } from "./upload/UploadManager.js";
import clone from "./utils/cloneDeep.js";
import { fieldTypes, guessType, isEqual } from "./utils/index.js";

let FileUploaderImpl: typeof FileUploader = FileUploader;
export function setFileUploaderClass(impl: typeof FileUploader) {
	FileUploaderImpl = impl;
}

let UploadManagerImpl: typeof UploadManager = UploadManager;
export function setUploadManagerClass(impl: typeof UploadManager) {
	UploadManagerImpl = impl;
}

export type RefreshDataSourceOptions<T> = {
	parameters?: Partial<RetrieveParameters<T>>;
	throw?: boolean;
};

type SavingState = {
	activeCalls: number;
	currentPromise: Promise<any> | null;
};

function objHasKeys(object: any) {
	return Object.keys(object).length > 0;
}

export type CurrentRow<T extends Record<string, unknown>> = StorageRecord<T>;
export type DataRecord<T extends Record<string, unknown>> = T & { [uid]: string | number };

export type DataObjectOptions<T extends Record<string, unknown>> = {
	allowDelete?: boolean;
	allowInsert?: boolean;
	allowUpdate?: boolean;
	articleId?: string;
	dataHandler?: DataHandler<T> | DataHandlerDescriptor;
	dataSourceId: string;
	disableAutoload?: boolean;
	dynamicLoading?: boolean;
	fields: FieldDefinition[];
	groupBy?: keyof T | null | string[];
	hasIDTrig?: boolean;
	hasIITrig?: boolean;
	hasIUTrig?: boolean;
	linkFields?: Array<keyof T> | Partial<Record<keyof T, string>> | null;
	masterDataObject?: AfDataObject<any> | null;
	optimisticLocking?: boolean;
	parameters?: RetrieveParameters<T>;
	selectFirstRow?: boolean;
	systemFieldNames?: Record<string, keyof T>;
	timeout?: number;
	uniqueIdField?: keyof T;
};

/**
 * This interface should be used to check compatability with the Omega DataObject
 * implementation. If we make changes to this interface, it should most likely be
 * listed as a breaking change in the README file.
 */
export interface AfDataObject<T extends Record<string, unknown>> extends EventTarget<DataObjectEventToValueMap<T>> {
	areParametersChanged: () => boolean;
	areRecordsSaving: () => boolean;
	beforeReload: () => Promise<boolean>;
	cancelEdit: () => boolean;
	cancelField: (field: keyof T) => boolean;
	canModifyCurrentRow: () => boolean;
	canModifyRow: (index: number) => boolean;
	clearFilter: () => void;
	currentRow(): CurrentRow<T>;
	currentRow<F extends keyof T>(field: F): T[F] | null;
	currentRow<F extends keyof T>(field: F, value: T[F]): boolean;
	dataLastLoaded: () => Date;
	deleteCurrentRow: () => Promise<false | number>;
	deleteRow: (index: number) => Promise<false | number>;
	endEdit: () => Promise<false | T>;
	getCurrentIndex: () => number;
	getData(): DataRecord<T>[];
	getData(index: number): DataRecord<T>;
	getData<F extends keyof T>(index: number, field: F): T[F];
	getDataLength: () => number;
	getDataSourceId: () => string;
	getExportURL: (format: "pdf" | "xlsx" | "csv" | "xls") => string;
	getFields(): FieldDefinition[];
	getFields(field: string): FieldDefinition | undefined;
	getFields(field: keyof T): FieldDefinition;
	getLinkFields: () => Partial<Record<keyof T, string>> | Array<keyof T> | null;
	getMasterDataObject: () => AfDataObject<any> | null;
	getParameter<P extends keyof RetrieveParameters<T>>(name: P): RetrieveParameters<T>[P];
	getUniqueIdField: () => keyof T;
	hasField: (field: string) => boolean;
	isAutoLoadDisabled: () => boolean;
	isDataLoaded: () => boolean;
	isDataLoading: () => boolean;
	isDeleteAllowed: () => boolean;
	isDeleteNeverAllowed: () => boolean;
	isDirty: (indexOfFieldName?: number | string) => boolean;
	isDynamicLoading: () => boolean;
	isInsertAllowed: () => boolean;
	isInsertNeverAllowed: () => boolean;
	isNewRecord: (index: number) => boolean;
	isNullable: (field: string) => boolean;
	isSaving: (index?: number) => boolean;
	isUpdateAllowed: () => boolean;
	isUpdateNeverAllowed: () => boolean;
	originalRow(): T;
	originalRow<F extends keyof T>(field: F): T[F];
	refreshCurrentRow: () => Promise<T | undefined>;
	refreshDataSource: () => Promise<void>;
	refreshRow: (index: number) => Promise<T | undefined>;
	removeRow: (index: number) => void;
	retrievePartial: (skip: number, maxRecords: number) => void;
	save: (data: Partial<T>) => void;
	setAllowDelete: (allow: boolean) => void;
	setAllowUpdate: (allow: boolean) => void;
	setAllowInsert: (allow: boolean) => void;
	setCurrentIndex(index: number): boolean;
	setCurrentIndex(index: number, waitForSave: true): Promise<boolean>;
	setCurrentIndex(index: number, waitForSave: false): boolean;
	setParameter<P extends keyof RetrieveParameters<T>>(name: P, value: RetrieveParameters<T>[P]): void;
}

type PartialWithUid<T> = Partial<T> & { [uid]: string | number };

/**
 * Data object capable of handling data from different sources.
 */
export class DataObject<T extends Record<string, unknown> = Record<string, unknown>>
	extends EventTarget<DataObjectEventToValueMap<T>>
	implements AfDataObject<T>
{
	readonly version = globalThis.__VERSION__;
	readonly dataHandler: DataHandler<T>;
	readonly masterDataObject: AfDataObject<any> | null;
	readonly options: Required<DataObjectOptions<T>>;
	readonly recordSource: RecordSource<T>;
	readonly storageEngine: Storage<T>;
	get fields(): FieldDefinition[] {
		return this.options.fields;
	}
	protected pagingComponent: Paging<T> | undefined;

	protected currentIndex: number;
	protected dataLoaded: number | null;
	protected dataLoading: boolean;
	protected dataSaving: boolean;
	protected dirtyRecords: Record<string | number, Partial<T>>;
	protected errorRecords: Record<number, boolean>;
	protected fieldNames: Array<keyof T>;
	protected newRecord: PartialWithUid<T>;
	protected parametersChanged: boolean;
	protected savingRecords: Record<number, SavingState>;
	protected temporarilyDisallowDelete: boolean;
	protected temporarilyDisallowInsert: boolean;
	protected temporarilyDisallowUpdate: boolean;

	constructor(options: DataObjectOptions<T>) {
		super(DataObjectEvents);
		let defaults = this.getDefaultOptions();
		this.options = {
			...defaults,
			...options,
			parameters: Object.assign({}, clone(defaults.parameters), clone(options.parameters)),
		} as Required<DataObjectOptions<T>>;

		this.currentRow = this.currentRow.bind(this);
		this.getData = this.getData.bind(this);
		this.getDirtyRecord = this.getDirtyRecord.bind(this);
		this.getFields = this.getFields.bind(this);
		this.getParameter = this.getParameter.bind(this);
		this.getSavedField = this.getSavedField.bind(this);
		this.originalRow = this.originalRow.bind(this);
		this.setCurrentIndex = this.setCurrentIndex.bind(this);
		this.setField = this.setField.bind(this);
		this.setParameter = this.setParameter.bind(this);
		this.storageRetrieve = this.storageRetrieve.bind(this);

		this.storageEngine = new MemoryStorage<T>(this.fields);
		this.currentIndex = -1;
		this.dataLoaded = null;
		this.dataLoading = false;
		this.dataSaving = false;
		this.dirtyRecords = {};
		this.errorRecords = {};
		this.fieldNames = this.options.fields.map((field) => (field.alias ?? field.aliasName ?? field.name) as keyof T);
		this.newRecord = { [uid]: this.storageEngine.getNewUid() } as PartialWithUid<T>;
		this.parametersChanged = false;
		this.savingRecords = {};
		this.temporarilyDisallowDelete = false;
		this.temporarilyDisallowUpdate = false;
		this.temporarilyDisallowInsert = false;

		this.recordSource = new RecordSource(this); // convenience functions for parameters

		this.dataHandler = this.handleDataHandlerOption(this.options);
		this.masterDataObject = this.handleMasterDataObjectOption();

		if (this.isDynamicLoading()) {
			this.getPagingComponent();
		}

		let preloadedData = globalThis.af?.article?.preloadedData?.[this.options.dataSourceId] as T[] | undefined;
		if (Array.isArray(preloadedData)) {
			this.handleRetrieveData(preloadedData);
			delete globalThis.af.article.preloadedData![this.options.dataSourceId];
		}
	}

	areParametersChanged = (): boolean => this.parametersChanged;
	areRecordsSaving = (): boolean => objHasKeys(this.savingRecords);

	async beforeReload() {
		return true;
	}

	dataLastLoaded = (): Date => new Date(this.dataLoaded || 0);
	getCurrentIndex = (): number => this.currentIndex;
	getDataLength = (): number => this.storageLength();

	/**
	 * Get what is set as the maximum rows to dataObject.
	 */
	getMaxRecords = (): number => this.options.parameters.maxRecords || -1;

	getPagingComponent = (): Paging<T> => {
		if (!this.pagingComponent) {
			this.pagingComponent = new Paging({ dataObject: this });
		}

		return this.pagingComponent;
	};

	hasDirtyRecords = (): boolean => objHasKeys(this.dirtyRecords);

	hasError = (index: number): boolean => {
		if (this.errorRecords[index]) {
			return this.errorRecords[index];
		} else {
			return false;
		}
	};

	isDataLoaded = (): boolean => this.dataLoaded !== null;
	isDataLoading = (): boolean => this.dataLoading;
	isDynamicLoading = (): boolean => this.options.dynamicLoading;

	/**
	 * Check if a record is a new record (ID field is null)
	 * @param index Index of the record to check
	 * @returns {boolean}
	 */
	isNewRecord = (index: number): boolean => {
		if (typeof index === "number" && index % 1 === 0) {
			if (index === -1) {
				return true;
			}

			if (this.fieldNames.indexOf(this.options.uniqueIdField) === -1) {
				return false;
			}

			return this.getSavedField(index, this.options.uniqueIdField) === null;
		}

		return false;
	};

	getFileStoreURL(record: Record<string, any>, type?: "download" | "view"): string;
	getFileStoreURL(primKey: string, fileName: string, type?: "download" | "view"): string;
	getFileStoreURL(
		primKeyOrRecord: string | Record<string, any>,
		fileNameOrType?: string | "download" | "view",
		type: "download" | "view" = "view",
	): string {
		if (typeof primKeyOrRecord === "object") {
			if (Object.hasOwn(primKeyOrRecord, "PrimKey") && Object.hasOwn(primKeyOrRecord, "FileName")) {
				return this.dataHandler.getFileStoreURL(
					primKeyOrRecord["PrimKey"] as string,
					primKeyOrRecord["FileName"],
					fileNameOrType as "download" | "view" | undefined,
				);
			}

			return "";
		}
		return this.dataHandler.getFileStoreURL(primKeyOrRecord, fileNameOrType ?? "file", type);
	}

	getBlobURL(record: Record<string, any>, field: string, type?: "download" | "view"): string;
	getBlobURL(primKey: string, field: string, fileName: string, type?: "download" | "view"): string;
	getBlobURL(
		primKeyOrRecord: Record<string, any> | string,
		field: string,
		fileNameOrType?: string | "download" | "view",
		type: "download" | "view" = "view",
	) {
		if (typeof primKeyOrRecord === "object") {
			if (Object.hasOwn(primKeyOrRecord, "PrimKey") && Object.hasOwn(primKeyOrRecord, "FileName")) {
				return this.dataHandler.getBlobURL(
					primKeyOrRecord["PrimKey"] as string,
					field,
					primKeyOrRecord["FileName"],
					fileNameOrType as "download" | "view" | undefined,
				);
			}

			return "";
		}

		return this.dataHandler.getBlobURL(primKeyOrRecord, field, fileNameOrType ?? "file", type);
	}

	getExportURL = (format: "pdf" | "xlsx" | "csv" | "xls"): string => this.dataHandler.getExportURL(format);

	protected storageCreate = (data?: T | null, uid?: string | number): number => this.storageEngine.create(data, uid);

	protected storageRetrieve(): StorageArray<T>;
	protected storageRetrieve(index: number): StorageRecord<T>;
	protected storageRetrieve<F extends keyof T>(index: number, field: F): T[F] | null;
	protected storageRetrieve<F extends keyof T>(
		index?: number,
		field?: F,
	): StorageArray<T> | StorageRecord<T> | T[F] | null {
		if (typeof index !== "number") {
			return this.storageEngine.retrieve();
		} else if (!field) {
			return index === -1 ? this.storageEngine.newRow(this.newRecord[uid]) : this.storageEngine.retrieve(index);
		} else {
			return index === -1 ? null : this.storageEngine.retrieve(index, field);
		}
	}

	protected storageUpdate = (index: number, data: T): boolean => this.storageEngine.update(index, data);
	protected storageDestroy = (index?: number): boolean => this.storageEngine.destroy(index);
	protected storageLength = (): number => this.storageEngine.length;

	cancelEdit = (): boolean => {
		const wasDirty = this.isDirty();
		this.makeClean(this.currentIndex);

		if (wasDirty) {
			this.fireEvent("onCancelEdit", this.currentIndex);
		}

		return wasDirty;
	};

	private canSetFieldValueToNull = (field: FieldDefinition, skipInsteadOfTriggerChecks = false): boolean => {
		if (!skipInsteadOfTriggerChecks) {
			if (this.isNewRecord(this.currentIndex)) {
				if (this.options.hasIITrig) {
					return true;
				}
			} else {
				if (this.options.hasIUTrig) {
					return true;
				}
			}
		}
		return (field.nullable || field.computed) ?? false;
	};

	clearFilter = (): void => {
		this.setParameters({ filterObject: null, filterString: "" });
		this.fireEvent("onClearFilter");
	};

	/**
	 * Returns the row at the current index
	 *
	 * @returns {CurrentRow<T>} Record
	 */
	currentRow(): CurrentRow<T>;
	/**
	 * Returns the value of a field in the record at the current index
	 * @param {F} field
	 * @returns {T[F]}
	 */
	currentRow<F extends keyof T>(field: F): T[F] | null;
	/**
	 * Sets the value of a field in the record at the current index
	 * @param {F} field Field whose value should change
	 * @param {T[F]} value Value to set on the field
	 * @returns {boolean} true if successful
	 */
	currentRow<F extends keyof T>(field: F, value: T[F]): boolean;
	currentRow(field?: keyof T, value?: T[keyof T]): CurrentRow<T> | T[keyof T] | boolean | null {
		if (field === undefined && value === undefined) {
			return clone(this.getDirtyRecord());
		} else if (field && value === undefined) {
			return clone(this.getDirtyRecord(field));
		} else if (field && value !== undefined) {
			const { dataSourceId } = this.options;

			if (this.isNewRecord(this.currentIndex)) {
				invariant(
					this.isInsertAllowed(),
					`Inserting is${this.isInsertNeverAllowed() ? "" : " currently"} not allowed on ${dataSourceId}`,
				);
			} else {
				invariant(
					this.isUpdateAllowed(),
					`Updating is${this.isUpdateNeverAllowed() ? "" : " currently"} not allowed on ${dataSourceId}`,
				);
			}

			const returnValue = this.setField(field, value);

			return returnValue;
		}

		return null;
	}

	private isValidData = (data: any): data is RetrieveResponse<T> => {
		if (Array.isArray(data)) {
			return true;
		} else if (typeof data === "object") {
			return typeof data.skip === "number" && (Array.isArray(data.data) || typeof data.total === "number");
		}

		return false;
	};

	private handleRetrieveDataError = (ex: any): void => {
		try {
			globalThis.onerror?.(ex.message, ex.fileName, ex.lineNumber, ex.columnNumber, ex);
		} catch {
			/* ignoring failures to report error */
		}

		const isIncorrectTypeError = new RegExp(errors.incorrectTypeOfField(this.getDataObjectIdentity(), '[^"]+')).test(
			ex.message,
		);

		if (isIncorrectTypeError) {
			ex.message = `Failed to parse record received from server. This is most likely because the record definition was changed. Try to reload the page to see if the problem is fixed.`;
		}

		this.fireEvent("onDataLoadFailed", ex.message);
	};

	private startRequest = (): { revert: () => any } => {
		let parametersWasChanged = this.parametersChanged;
		this.parametersChanged = false;

		return {
			revert: () => {
				this.parametersChanged = parametersWasChanged;
			},
		};
	};

	private previousRetrieveRequest: AbortController | null = null;
	private dataHandlerRetrieve = async (params: RetrieveParameters<T> | PagedRetrieveParameters<T>): Promise<void> => {
		const { revert } = this.startRequest();
		const eventResult = this.fireEvent("onBeforeLoad", params);

		if (eventResult === false) {
			// NOTE: Not the best solution - would be better to have an event that fires
			//       after onBeforeUnload that could be used for working indicators and such
			this.fireEvent("onDataLoadFailed", null);
			revert();
			return;
		}

		if (this.dataSaving) {
			const savingStatuses = Object.values(this.savingRecords);

			await Promise.all(savingStatuses.filter((s) => s.currentPromise !== null).map((s) => s.currentPromise));
		}

		let shouldSetDataLoading = true;
		try {
			const controller = new AbortController();

			if (this.previousRetrieveRequest !== null) {
				this.previousRetrieveRequest.abort();
			}
			this.previousRetrieveRequest = controller;

			this.dataLoading = true;
			this.fireEvent("onDataLoading", null);

			let result = await this.dataHandler.retrieve({ ...params, returnTotal: false }, { signal: controller.signal });

			if (this.previousRetrieveRequest === controller) {
				this.previousRetrieveRequest = null;
			} else {
				shouldSetDataLoading = false;
			}

			if (shouldSetDataLoading) {
				this.dataLoading = false;
			}

			if (!controller.signal.aborted) {
				invariant(
					this.isValidData(result),
					`Invalid data from server. Expected array or paging object, got ${typeof result}`,
				);

				try {
					this.handleRetrieveData(result);

					if (!Array.isArray(result) && typeof (result as PagedRetrieveResponse<T>).skip === "number") {
						this.dataHandlerRetrieveCounter(params as PagedRetrieveParameters<T>, result);
					}
				} catch (ex: any) {
					this.handleRetrieveDataError(ex);

					throw new Error(ex.message);
				}
			}

			return;
		} catch (ex: any) {
			if (shouldSetDataLoading) {
				this.dataLoading = false;
			}
			if (ex.name === "AbortError") {
				return;
			}

			revert();

			if (process.env.NODE_ENV !== "test") {
				console.error(ex);
				logError(ex);
			}

			// This should only happen on network failures (offline, navigate, cors etc)
			// Should not throw an error, since this can cause an alert to pop up if the
			// user navigates away while a request is pending.
			this.fireEvent("onDataLoadFailed", ex.message);
		}
	};

	private previousRowCountRequest: AbortController | null = null;
	dataHandlerRetrieveCounter = async (
		params: PagedRetrieveParameters<T>,
		response: PagedRetrieveResponse<T>,
	): Promise<void> => {
		if (this.dataSaving) {
			// Delay loading until after save is complete
			await Promise.all(
				Object.values(this.savingRecords)
					.filter((record) => record.currentPromise !== null)
					.map((record) => record.currentPromise),
			);
		}

		try {
			const controller = new AbortController();

			if (this.previousRowCountRequest !== null) {
				this.previousRowCountRequest.abort();
			}
			this.previousRowCountRequest = controller;

			const result = (await this.dataHandler.rowcount(params, { signal: controller.signal })) as
				| RequestError
				| PagedRetrieveResponse<T>;

			if (this.previousRowCountRequest === controller) {
				this.previousRowCountRequest = null;
			}

			if (controller.signal.aborted) {
				return;
			}

			invariant(this.isValidData(result), "The data retrieved by the DataObject was not an Array");
			try {
				let dataLength = this.getDataLength();
				if (result.total > dataLength) {
					this.storageEngine.length = result.total;
				}

				this.dataLoaded = Date.now();
				this.fireEvent("onRowCountLoaded", {
					skip: result.skip,
					count: response.data.length,
					total: result.total,
				});
			} catch (ex: any) {
				this.handleRetrieveDataError(ex);

				throw new Error(ex.message);
			}
		} catch (ex: any) {
			if (ex.name === "AbortError") {
				return;
			}
			// This should only happen on network failures (offline, navigate, cors etc)
			// Should not throw an error, since this can cause an alert to pop up if the
			// user navigates away while a request is pending.
			this.fireEvent("onDataLoadFailed", ex.message);

			return;
		}

		return;
	};

	private getDefaultOptions = (): Partial<DataObjectOptions<T>> => {
		const { af } = globalThis;

		return {
			articleId: af?.article?.id,
			timeout: 0,
			linkFields: null,
			disableAutoload: false,
			dynamicLoading: false,
			allowDelete: false,
			allowUpdate: false,
			allowInsert: false,
			selectFirstRow: true,
			optimisticLocking: false,
			hasIITrig: false,
			hasIUTrig: false,
			hasIDTrig: false,
			parameters: {
				filterString: "",
				whereClause: "",
				filterObject: null,
				whereObject: null,
				sortOrder: [],
				maxRecords: 50,
				distinctRows: false,
				masterChildCriteria: {},
			},
			systemFieldNames: {},
			masterDataObject: null,
			groupBy: null,
			uniqueIdField: "PrimKey" as keyof T,
			fields: [],
		};
	};

	/**
	 * Delete the currently selected current row.
	 *
	 * @param {Callback<number | false>} callback
	 * @returns {false | Promise<void>}
	 */
	deleteCurrentRow = (): Promise<false | number> => this.deleteRow(this.currentIndex);

	deleteRow = async (index: number): Promise<number | false> => {
		invariant(!this.isDeleteNeverAllowed(), "Deleting is not allowed on this DataObject");
		invariant(this.isDeleteAllowed(), "Deleting is currently not allowed on this DataObject");

		const eventResult = this.fireEvent("onRecordDeleting", index);
		const { uniqueIdField } = this.options;

		if (eventResult === false) {
			return false;
		}

		const uniqueId = this.storageRetrieve(index, uniqueIdField);

		if (uniqueId === null) {
			this.removeRow(index);

			return index;
		} else {
			const destroyParams: Partial<T> = {};
			destroyParams[uniqueIdField] = uniqueId as T[keyof T];

			if (await this.dataHandler.destroy(destroyParams)) {
				const records = this.storageRetrieve();

				for (let i = 0; i < records.length; i++) {
					let row = records[i];
					if (isEqual(row[uniqueIdField], uniqueId)) {
						this.removeRow(i);

						return i;
					}
				}
			}
		}

		return false;
	};

	/**
	 * Creates a new record for the data object without adding it to storage.
	 * Can be added later using 'addRow'. Used to get a row with a unique ID
	 * that will be persisted after saving.
	 *
	 * @param data
	 * @returns
	 */
	newRow = (data?: Partial<T>): Partial<T> & { [uid]: string | number } =>
		({
			[uid]: this.storageEngine.getNewUid(),
			...data,
		}) as Partial<T> & { [uid]: string | number };

	/**
	 *
	 * @param row
	 * @returns
	 */
	addRow = async (row: Partial<T> & { [uid]?: number | string }): Promise<DataRecord<T> | null> => {
		invariant(
			this.isInsertAllowed(),
			`Inserting is${this.isInsertNeverAllowed() ? "" : " currently"} not allowed on ${this.getDataSourceId()}`,
		);

		let id = row[uid];
		let newRow = {
			...row,
			...this.options.parameters.masterChildCriteria,
		};
		let eventResult = this.fireEvent("onBeforeSave", newRow);

		if (eventResult === false) {
			return null;
		}

		let record = await this.dataHandler.create(newRow);

		if (record) {
			let index = this.storageEngine.create(record, id);
			this.fireEvent("onRecordCreated", index);
			this.fireEvent("onAfterSave", index);
			return this.getData(index);
		}

		return record;
	};

	/**
	 * Updates a row with the provided data. Can be used if data is updated outside
	 * the DataObject (e.g. by another DataObject or Procedure), and you want to
	 * update the data in the DataObject storage without reloading all data.
	 *
	 * For example, in some cases you might call dataHandler directly to update data,
	 * to avoid changing the current index. You can then call this method to update
	 * the data in the DataObject storage with the return value of the dataHandler call.
	 *
	 * @param index Index of the row to update
	 * @param data Data to update on the row
	 * @returns
	 */
	updateRow = async (index: number, data: Partial<T>): Promise<DataRecord<T> | null> => {
		invariant(
			this.isUpdateAllowed(),
			`Updating is${this.isUpdateNeverAllowed() ? "" : " currently"} not allowed on ${this.getDataSourceId()}`,
		);

		let existingRecord = this.getData(index);
		let updatedRecord = {
			...existingRecord,
			...data,
		} satisfies T;

		this.storageUpdate(index, updatedRecord);
		this.makeClean(index); // will also fire onRecordRefreshed

		return updatedRecord;
	};

	endEdit = async (): Promise<T | false> => {
		if (!this.isDirty()) {
			return false;
		}

		if (this.options.masterDataObject?.isDirty()) {
			await this.options.masterDataObject.endEdit();
		}

		let index = this.currentIndex;
		if (index !== -1 && Object.hasOwn(this.savingRecords, index)) {
			if (this.savingRecords[index].activeCalls) {
				await this.savingRecords[index].currentPromise;
				return this.endEdit();
			}
		}

		let startTimestamp = Date.now();
		let idField = this.options.uniqueIdField;
		let hasIdField = this.fieldNames.indexOf(idField) >= 0;
		let id = index === -1 ? null : this.getSavedField(index, idField);
		let dirtyRecord: PartialWithUid<T> | Partial<T>;
		let record: Partial<T>;
		let dataHandlerFunc: "create" | "update";

		if (id === null) {
			dirtyRecord = index === -1 ? this.newRecord : this.dirtyRecords[index];
			record = Object.assign({}, dirtyRecord, this.options.parameters.masterChildCriteria);
			dataHandlerFunc = "create";
		} else {
			dirtyRecord = this.dirtyRecords[index];
			record = Object.assign({}, dirtyRecord);
			let savedId = this.getSavedField(index, idField);
			if (savedId !== null) {
				record[idField] = savedId;
			}

			dataHandlerFunc = "update";
		}

		let eventResult = this.fireEvent("onBeforeSave", record);
		let updatedFieldName = this.options.systemFieldNames["updated"] || ("Updated" as keyof T);

		if (this.options.optimisticLocking === true && this.fieldNames.indexOf(updatedFieldName) !== -1 && index !== -1) {
			record[updatedFieldName] = this.currentRow(updatedFieldName) as T[keyof T];
		}

		if (eventResult === false) {
			return false;
		}

		this.fireEvent("onSaving");

		let validationResult = this.validateSavingData(record);
		if (validationResult) {
			this.fireEvent("onSaveFailed", index);
			throw new Error(validationResult);
		}

		let normalRecord = Object.assign({}, dirtyRecord);

		// Remove any field if its definition says to ignore it or it has an alias
		for (let field of this.fields) {
			if (field.ignored || field.alias || field.aliasName) {
				const prop = (field.alias ?? field.aliasName ?? field.name) as keyof T;
				if (field.name !== this.options.uniqueIdField && Object.hasOwn(record, prop)) {
					delete record[prop];
				}

				if (Object.hasOwn(normalRecord, prop)) {
					delete normalRecord[prop];
				}
			}
		}

		if (Object.keys(normalRecord).length > 0) {
			let newIndex: number | null;
			if (index === -1) {
				newIndex = this.storageCreate(null, this.newRecord[uid]);
				delete this.dirtyRecords[newIndex]; // Temporarily mark the record as clean until save is complete
				this.currentIndex = newIndex;
				this.fireEvent("onRecordCreated", newIndex);
				this.makeClean(-1);
			} else {
				newIndex = index;
				delete this.dirtyRecords[index]; // Set the dirty status of the record to not-dirty while saving
			}

			let savingState: SavingState;
			if (!Object.hasOwn(this.savingRecords, newIndex)) {
				savingState = {
					activeCalls: 1,
					currentPromise: null,
				};
				this.savingRecords[index] = savingState;
			} else {
				savingState = this.savingRecords[index];
				savingState.activeCalls++;
			}

			this.dataSaving = true;
			savingState.currentPromise = this.dataHandler[dataHandlerFunc](record, undefined);

			let processSavingState = () => {
				if (newIndex !== null) {
					delete this.savingRecords[newIndex];
				}

				if (Object.keys(this.savingRecords).length === 0) {
					this.dataSaving = false;
				}
			};

			let result: T | null;
			try {
				result = await savingState.currentPromise;

				// If data was reloaded after endEdit was called and completed before this callback
				// we need to look for a row having the same PrimKey/UniqueKey, and abort if not found
				if (startTimestamp < (this.dataLoaded ?? 0)) {
					if (id === null) {
						// If row did not have a unqiue id
						// we cannot test if the record is the same
						// so we set index to null
						newIndex = null;
					} else {
						if (newIndex >= this.getDataLength() || (hasIdField && this.storageRetrieve(newIndex, idField) !== id)) {
							newIndex = this.getIndexFromUniqueId(id);
						}
					}
				}

				if (newIndex !== null) {
					// If we have the index of the saved record
					delete this.errorRecords[index];

					if (result !== null) {
						this.storageUpdate(newIndex, result);
					} else {
						console.warn("Statement did not return any values, nothing was updated");
					}

					let data = this.storageRetrieve(newIndex) as T;

					if (!this.isDirty(newIndex)) {
						this.makeClean(newIndex, true);
					}

					processSavingState();
					this.fireEvent("onAfterSave", newIndex);

					return data;
				} else {
					delete this.errorRecords[index];
				}
			} catch (error) {
				if (newIndex !== null && !this.isDirty(newIndex)) {
					this.dirtyRecords[newIndex] = dirtyRecord;
				}

				if (newIndex !== null) {
					if (hasIdField && this.storageRetrieve(newIndex, idField) !== id) {
						newIndex = this.getIndexFromUniqueId(id as T[keyof T]);
					}
					if (newIndex) {
						this.dirtyRecords[newIndex] = dirtyRecord;
					}
				}

				processSavingState();

				if (newIndex != null) {
					this.errorRecords[newIndex] = true;
					this.fireEvent("onSaveFailed", newIndex);
				}

				if ((error as any)?.name !== "AbortError") {
					throw error;
				}
			} finally {
				savingState.currentPromise = null;
				savingState.activeCalls--;
			}
		} else {
			this.makeClean(index);
			return false;
		}

		return false;
	};

	/**
	 * Resets a field to the value currently saved in the database
	 *
	 * @param {keyof T} field Field to reset
	 * @returns {boolean} True if successful
	 */
	cancelField = (field: keyof T): boolean =>
		this.setField(field, this.getSavedField(this.currentIndex, field) as T[keyof T]);
	protected getFieldIndex = (fieldName: keyof T): number =>
		this.fields.findIndex((f) => (f.alias ?? f.aliasName ?? f.name) === fieldName);

	getFieldDefinition = (field: string): FieldDefinition | undefined =>
		this.fields.find((f) => (f.alias ?? f.aliasName ?? f.name) === field);

	/**
	 * Get the definition of all fields in the data object
	 *
	 * @return {FieldDefinition[]} All field definitions
	 */
	getFields(): FieldDefinition[];
	/**
	 * Get the definition of a single field in the data object if it exists,
	 * otherwise undefined
	 * @param {string} field - Field whose definition to get
	 */
	getFields(field: string): FieldDefinition | undefined;
	getFields(field: keyof T): FieldDefinition;
	getFields(fieldName?: keyof T | string): FieldDefinition[] | FieldDefinition | undefined {
		if (fieldName) {
			for (let i = 0; i < this.fields.length; i++) {
				let field = this.fields[i];
				const prop = field.alias ?? field.aliasName ?? field.name;
				if (prop === fieldName) {
					return Object.assign({}, field, {
						caption: getLocalizedString(field.caption || field.name) as string,
					});
				}
			}

			return undefined;
		} else {
			return this.fields.map((field) =>
				Object.assign({}, field, {
					caption: getLocalizedString(field.caption || field.name) as string,
				}),
			);
		}
	}

	/**
	 * Creates a `FileUploader` instance that uploads files to this data source.
	 * If the {fieldName} param is provided, files will be uploaded as blobs to that
	 * field.
	 *
	 * @deprecated Use {@link getUploadManager} instead.
	 * @param {string | undefined} fieldName
	 */
	getUploader(fieldName?: string) {
		const articleId = this.options.articleId.replace(/\.\d+$/, "");
		return new FileUploaderImpl({ dataObject: this, articleId, fieldName });
	}

	/**
	 * Creates an `UploadManager` instance that uploads files to this data source.
	 * If the {fieldName} param is provided, files will be uploaded as blobs to that
	 * field.
	 *
	 * @param {string | undefined} fieldName
	 */
	getUploadManager(fieldName?: string) {
		return new UploadManagerImpl({ dataHandler: this.dataHandler, fieldName });
	}

	hasField = (fieldName: string): boolean => this.fields.some((f) => (f.alias ?? f.aliasName ?? f.name) === fieldName);

	/**
	 * Checks if a particular field can be set to null . This will in most cases reflect the logic of sql server.
	 */
	isNullable = (fieldName: string): boolean => {
		for (let field of this.fields) {
			if ((field.alias ?? field.aliasName ?? field.name) === fieldName) {
				return this.canSetFieldValueToNull(field);
			}
		}

		return false;
	};

	private setField<F extends keyof T>(field: F, value: T[F]): boolean {
		const fieldIndex = this.getFieldIndex(field);
		const fieldDefinition = this.fields[fieldIndex];

		invariant(fieldDefinition, `Field '${field as string}' does not exist in the data object`);

		// Make empty strings null
		if (typeof value === "string" && value === "") {
			(value as T[F] | null) = null;
		}

		// Check validation
		invariant(
			fieldTypes[this.getFieldType(fieldDefinition, value)].validate(value),
			`Supplied field '${field as string}' was of incorrect type`,
		);

		let dirtyRecord = this.currentIndex === -1 ? this.newRecord : this.dirtyRecords[this.currentIndex];

		const fieldValue = this.storageRetrieve(this.currentIndex, field);

		const prop = (fieldDefinition.alias ?? fieldDefinition.aliasName ?? fieldDefinition.name) as keyof T;

		if (isEqual(fieldValue, value)) {
			if (this.isDirty(prop)) {
				delete dirtyRecord[prop];
				if (!objHasKeys(dirtyRecord)) {
					if (this.currentIndex !== -1) {
						delete this.dirtyRecords[this.currentIndex];
					}
					this.fireEvent("onDirtyChanged", false);
				}
				this.fireEvent("onFieldChanged", {
					name: field,
					value,
				});

				return true;
			} else {
				return false; // nothing happend, same value
			}
		} else {
			if (dirtyRecord && isEqual(dirtyRecord[prop], value)) {
				return false; // nothing happend, same value
			} else {
				const wasDirty = dirtyRecord && objHasKeys(dirtyRecord);

				if (this.currentIndex !== -1 && !dirtyRecord) {
					this.dirtyRecords[this.currentIndex] = dirtyRecord = {};
				}

				dirtyRecord[prop] = value;

				if (!wasDirty) {
					this.fireEvent("onDirtyChanged", true);
				}

				this.fireEvent("onFieldChanged", {
					name: field,
					value,
				});

				return true;
			}
		}
	}

	private getSavedField<F extends keyof T>(index: number, field: F): T[F] | null {
		return this.storageRetrieve(index, field);
	}

	/**
	 * Returns all data currently loaded in the data object
	 */
	getData(): DataRecord<T>[];
	/**
	 * Returns the record at the specified index. Throws an error if index is out of range.
	 * @param index
	 */
	getData(index: number): DataRecord<T>;
	/**
	 * Returns the value of the specified field from the record at the specified index. Throws an
	 * error if the index is out of range, or the field does not exist.
	 * @param {number} index
	 * @param {string} field
	 */
	getData<F extends keyof T>(index: number, field: F): T[F];
	getData<F extends keyof T>(index?: number, fieldName?: F) {
		if (index !== undefined) {
			this.checkIndexRange(index);
			if (fieldName !== undefined) {
				invariant(
					this.hasField(fieldName as string),
					`${this.getDataObjectIdentity()} does not have a field named '${fieldName as string}'`,
				);

				return this.storageRetrieve(index, fieldName);
			} else {
				return this.storageRetrieve(index);
			}
		} else {
			return this.storageRetrieve();
		}
	}

	protected getDataObjectIdentity = () => {
		if (this.options.dataSourceId) {
			return `The data object ${this.options.dataSourceId}`;
		} else {
			return "This data object";
		}
	};

	getDirtyRecord(): CurrentRow<T>;
	getDirtyRecord<K extends keyof T>(field: K): T[K] | null;
	getDirtyRecord<K extends keyof T>(field?: K): CurrentRow<T> | T[K] | null {
		const dirtyRecord =
			(this.currentIndex === -1 ? this.newRecord : this.dirtyRecords[this.currentIndex]) || ({} as Partial<T>);

		if (typeof field !== "string") {
			let current: any = this.storageRetrieve(this.currentIndex) ?? ({} as Partial<T>);

			return {
				...current,
				...dirtyRecord,
				[uid]: current?.[uid] ?? (dirtyRecord as StorageRecord<Partial<T>>)[uid],
			} as StorageRecord<T>;
		} else {
			if (Object.hasOwn(dirtyRecord, field)) {
				return dirtyRecord[field] ?? null;
			} else {
				return this.storageRetrieve(this.currentIndex, field);
			}
		}
	}

	getIndexFromUniqueId = (uniqueId: T[keyof T]): number | null => {
		const rowCount = this.getDataLength();

		for (let i = 0; i < rowCount; i++) {
			if (this.storageRetrieve(i, this.options.uniqueIdField) === uniqueId) {
				return i; // Return index if found
			}
		}

		return null;
	};

	private handleDataHandlerOption = (options: DataObjectOptions<T>): DataHandler<T> => {
		const { articleId, dataHandler, dataSourceId, timeout } = options;

		// If a valid DataHandler instance is specified - use that
		if (dataHandler instanceof DataHandler) {
			return dataHandler as DataHandler<T>;
		}

		// Else if an object defining a DataHandler subclass is specified
		if (dataHandler && typeof dataHandler === "object") {
			if (Object.hasOwn(dataHandler, "type") && Object.hasOwn(dataHandler, "options")) {
				if (typeof dataHandler.type !== "function" && globalThis.af?.data) {
					const Handler = (globalThis.af.data as any)[dataHandler.type] as { new (options?: any): DataHandler<any> };

					if (typeof Handler === "function") {
						// Try to make a new DataHandler of that type using supplied options
						return new Handler(dataHandler.options);
					}
				} else if (typeof dataHandler.type === "function") {
					const Handler = dataHandler.type as { new (options?: any): DataHandler<any> };
					return new Handler(dataHandler.options);
				}
			}
		}

		return new DataProviderHandler({ articleId, dataSourceId, fields: options.fields, timeout });
	};

	private handleRetrieveData = (response: RetrieveResponse<T>) => {
		const wasDirty = this.isDirty();
		if (Array.isArray(response)) {
			this.storageDestroy();
			this.dirtyRecords = {};
			this.storageEngine.create(response);

			this.dataLoaded = Date.now();
			this.currentIndex = this.options.selectFirstRow && response.length ? 0 : -1;

			this.fireEvent("onDataLoaded", response.length);
			this.fireEvent("onCurrentIndexChanged", this.currentIndex);

			if (wasDirty) {
				this.fireEvent("onDirtyChanged", false);
			}
		} else if (typeof response.skip === "number" && Array.isArray(response.data)) {
			if (response.skip === 0) {
				this.storageDestroy();
				this.dirtyRecords = {};
				const newData: T[] = [];

				for (let i = 0; i < response.data.length; i++) {
					newData[i + response.skip] = response.data[i];
				}

				for (let record of newData) {
					this.storageCreate(record);
				}

				this.dataLoaded = Date.now();

				this.fireEvent("onPartialDataLoaded", {
					skip: response.skip,
					count: response.data.length,
					total: response.total,
				});

				this.setIndexTo(this.options.selectFirstRow && newData.length ? 0 : -1);

				if (wasDirty) {
					this.fireEvent("onDirtyChanged", false);
				}
			} else {
				for (let i = 0; i < response.data.length; i++) {
					this.storageUpdate(i + response.skip, response.data[i]);
					delete this.dirtyRecords[i + response.skip];
				}

				this.dataLoaded = Date.now();
				this.fireEvent("onPartialDataLoaded", {
					skip: response.skip,
					count: response.data.length,
					total: response.total,
				});
			}
		} else {
			throw new TypeError("Invalid pData parameter passed to handleRetrieveData");
		}
	};

	/**
	 * Returns the value of the first record in the data object where predicate is true, and undefined
	 * otherwise.
	 * @param predicate find calls predicate once for each record of the data object, in ascending
	 * order, until it finds one where predicate returns true. If such a rcord is found, find
	 * immediately returns that record value. Otherwise, find returns undefined.
	 */
	find = (
		predicate: (value: DataRecord<T>, index: number, obj: DataRecord<T>[]) => unknown,
	): DataRecord<T> | undefined => this.getData().find(predicate);

	/**
	 * Returns the index of the first record in the data object where predicate is true, and -1
	 * otherwise.
	 * @param predicate find calls predicate once for each record of the data object, in ascending
	 * order, until it finds one where predicate returns true. If such a record is found,
	 * findIndex immediately returns that record index. Otherwise, findIndex returns -1.
	 */
	findIndex = (predicate: (value: DataRecord<T>, index: number, obj: StorageArray<T>) => unknown): number =>
		this.getData().findIndex(predicate);

	/**
	 * Returns the records of the data object that meet the condition specified in a callback function.
	 * @param predicate A function that accepts up to three arguments. The filter method calls the predicate function one time for each element in the array.
	 */
	filter = (predicate: (value: DataRecord<T>, index: number, data: DataRecord<T>[]) => unknown): DataRecord<T>[] =>
		this.getData().filter(predicate);

	/**
	 * Calls a defined callback function on each element of an array, and returns an array that contains the results.
	 * @param callbackfn A function that accepts up to three arguments. The map method calls the callbackfn function one time for each element in the array.
	 */
	map = <U>(callbackfn: (value: DataRecord<T>, index: number, array: DataRecord<T>[]) => U): U[] =>
		this.getData().map(callbackfn);

	/**
	 * Calls a defined callback function on each element of an array, and returns an array that contains the results.
	 * @param callbackfn A function that accepts up to three arguments. The map method calls the callbackfn function one time for each element in the array.
	 * */
	forEach = (callbackFn: (value: DataRecord<T>, index: number, array: DataRecord<T>[]) => void) =>
		this.getData().forEach(callbackFn);

	isDirty = (indexOrFieldName?: number | string | symbol): boolean => {
		if (indexOrFieldName === undefined) {
			const dirtyRecord = this.currentIndex === -1 ? this.newRecord : this.dirtyRecords[this.currentIndex];

			return !!(dirtyRecord && objHasKeys(dirtyRecord));
		}

		if (typeof indexOrFieldName === "number" && indexOrFieldName % 1 === 0) {
			const dirtyRecord = indexOrFieldName === -1 ? this.newRecord : this.dirtyRecords[indexOrFieldName];

			return !!(dirtyRecord && objHasKeys(dirtyRecord));
		} else if (typeof indexOrFieldName === "string") {
			const dirtyRecord = this.currentIndex === -1 ? this.newRecord : this.dirtyRecords[this.currentIndex];

			return !!(dirtyRecord && Object.hasOwn(dirtyRecord, indexOrFieldName));
		}

		return false;
	};

	isSaving = (index?: number): boolean => {
		if (index === undefined) {
			return Object.hasOwn(this.savingRecords, this.currentIndex);
		}

		if (typeof index === "number" && index % 1 === 0) {
			return Object.hasOwn(this.savingRecords, index);
		}

		return false;
	};

	private makeClean = (pIndex?: number, pWasDirty?: boolean) => {
		const index = pIndex !== undefined ? pIndex : this.getCurrentIndex();
		const wasDirty = pWasDirty !== undefined ? pWasDirty : this.isDirty();

		if (index === -1) {
			this.newRecord = { [uid]: this.storageEngine.getNewUid() } as PartialWithUid<T>;
		} else {
			const { uniqueIdField } = this.options;
			const hasUniqueIdField = this.fieldNames.includes(uniqueIdField);

			if (hasUniqueIdField && this.getSavedField(index, uniqueIdField) === null) {
				this.removeRow(index); // Removes the row internally and fires onCurrentIndexChanged

				return; // Stop here, so onRecordRefreshed does not get fired
			} else {
				delete this.dirtyRecords[index];
				delete this.errorRecords[index];
			}
		}

		if (index === this.currentIndex && wasDirty) {
			this.fireEvent("onDirtyChanged", false);
		}

		this.fireEvent("onRecordRefreshed", index);
	};

	getMasterDataObject = (): AfDataObject<T> | null => this.options.masterDataObject;

	private refreshMasterChildCriteria = (masterDataObject: AfDataObject<any>) => {
		const { linkFields, parameters } = this.options;
		const masterChildCriteria: Partial<T> = parameters.masterChildCriteria || {};
		const masterRow = masterDataObject.currentRow();
		let changed = false;

		invariant(linkFields, "Specified linkFields was not an object or array");
		if (Array.isArray(linkFields)) {
			for (let fieldName of linkFields) {
				if (Object.hasOwn(masterRow, fieldName) && masterChildCriteria[fieldName as keyof T] !== masterRow[fieldName]) {
					masterChildCriteria[fieldName] = masterRow[fieldName];
					changed = true;
				}
			}
		} else {
			for (let childName of Object.keys(linkFields)) {
				let masterName = linkFields[childName as keyof T] as string;
				if (
					Object.hasOwn(masterRow, masterName) &&
					masterChildCriteria[childName as keyof T] !== masterRow[masterName]
				) {
					masterChildCriteria[childName as keyof T] = masterRow[masterName];
					changed = true;
				}
			}
		}

		if (changed) {
			this.options.parameters.masterChildCriteria = masterChildCriteria;
			this.parametersChanged = true;
			this.fireEvent("onParameterUpdated", {
				name: "masterChildCriteria",
				value: masterChildCriteria,
			});
		}
	};

	private bindToMaster = (masterDataObject: AfDataObject<any>) => {
		if (typeof masterDataObject === "string")
			masterDataObject = globalThis[masterDataObject] ?? globalThis.af?.article?.dataObjects?.[masterDataObject];
		if (!masterDataObject) return;

		masterDataObject.attachEvent("onCurrentIndexChanging", async (event: CustomEvent<number>) => {
			let { detail: index } = event;
			let evtResult = this.fireEvent("onMasterIndexChanging", index);
			if (evtResult === false) {
				return false;
			}

			try {
				await this.endEdit();
			} catch {
				return false;
			}
		});

		masterDataObject.attachEvent("onBeforeLoad", async () => {
			let evtResult = this.fireEvent("onMasterBeforeLoad", null);
			if (evtResult === false) {
				return false;
			}

			try {
				await this.endEdit();
			} catch {
				return false;
			}
		});

		masterDataObject.attachEvent("onCurrentIndexChanged", async (event: CustomEvent<number>) => {
			let { detail: index } = event;
			this.refreshMasterChildCriteria(masterDataObject);

			if (index === -1) {
				// Fake loading of no records
				let evtResult = this.fireEvent("onBeforeLoad", this.options.parameters);

				if (evtResult === false) {
					return false;
				}

				this.previousRetrieveRequest?.abort();
				this.previousRowCountRequest?.abort();
				this.handleRetrieveData([]); // Clear our internal data
			} else if (this.options.disableAutoload !== true) {
				this.refreshDataSource();
			}
		});

		masterDataObject.attachEvent("onFieldChanged", () => {
			this.refreshMasterChildCriteria(masterDataObject);
		});

		masterDataObject.attachEvent("onAfterSave", (event: CustomEvent<number>) => {
			let { detail: index } = event;

			if (this.getCurrentIndex() === index) {
				this.refreshMasterChildCriteria(masterDataObject);

				if (this.areParametersChanged() && this.options.disableAutoload !== true) {
					this.refreshDataSource();
				}
			}
		});

		this.refreshMasterChildCriteria(masterDataObject);
	};

	private handleMasterDataObjectOption = (): AfDataObject<T> | null => {
		// Bind to masterDataObject if any
		let { linkFields, masterDataObject } = this.options;

		if (this.options.masterDataObject) {
			if (
				typeof masterDataObject === "string" &&
				Object.hasOwn(globalThis, masterDataObject) &&
				typeof globalThis[masterDataObject] === "object"
			) {
				this.options.masterDataObject = globalThis[masterDataObject] as unknown as AfDataObject<any>;
			}

			invariant(linkFields, "LinkFields was not specified in the options");
			if (Array.isArray(linkFields)) {
				invariant(linkFields.length !== 0, "No fields found in specified linkFields");
			} else if (typeof linkFields === "object") {
				invariant(Object.keys(linkFields).length !== 0, "No fields found in specified linkFields");
			} else {
				throw new TypeError("Specified linkFields was not an object or array");
			}

			this.bindToMaster(this.options.masterDataObject);

			return this.options.masterDataObject;
		}

		return null;
	};

	/**
	 * Provides the name of the data object.
	 */
	getDataSourceId = (): string => this.options.dataSourceId;

	/**
	 * Get what is set as the maximum rows to dataObject.
	 */
	getLinkFields = (): Array<keyof T> | Partial<Record<keyof T, string>> | null => this.options.linkFields;

	/**
	 * Get unique field of the data object.
	 */
	getUniqueIdField = (): keyof T => this.options.uniqueIdField;

	isAutoLoadDisabled = (): boolean => this.options.disableAutoload;

	originalRow(): T;
	originalRow<F extends keyof T>(field: F): T[F];
	originalRow<F extends keyof T>(field?: F): T | T[F] {
		if (field === undefined) {
			return this.storageRetrieve(this.currentIndex) as T;
		} else {
			return this.getSavedField(this.currentIndex, field) as T[F];
		}
	}

	/**
	 * Get a data filtering parameter value
	 *
	 * @param {P} name Parameter to get current value for
	 */
	getParameter<P extends keyof RetrieveParameters<T>>(name: P): RetrieveParameters<T>[P] {
		const { parameters } = this.options;
		invariant(Object.hasOwn(parameters, name), "Invalid parameter name specified when calling getParameter");
		return clone(parameters[name]);
	}

	/**
	 * Set a parameter value for data filtering.
	 *
	 * @param {P} name Name of the parameter
	 * @param {RetrieveParameters<T>[P]} value Parameter value to set
	 */
	setParameter<P extends keyof RetrieveParameters<T>>(name: P, value: RetrieveParameters<T>[P]): void {
		const { parameters } = this.options;

		if (name === "filterString" || name === "whereClause") {
			if (!value) {
				value = "";
			} // Let the filterString/whereClause be empty string if it is falsy

			const parameter = name === "filterString" ? "filterObject" : "whereObject";
			this.setParameter(parameter, null); // Clear filterObject/whereObject when clearing the string equivalent
		}

		invariant(
			Object.hasOwn(parameters, name) && name !== "masterChildCriteria",
			`Invalid parameter specified when calling setParameter: ${name}`,
		);

		const parameter = (parameters as any)[name];

		invariant(typeof parameter === typeof value, `Invalid type of the supplied parameter value: ${typeof value}`);

		if (!isEqual(parameter, value)) {
			parameters[name] = value;
			this.parametersChanged = true;

			if (name === "maxRecords" && globalThis.af?.common?.localStorage) {
				globalThis.af.common.localStorage.set(
					`${globalThis.af?.article.id}-${this.getDataSourceId()}-pageSize`,
					value,
					false,
				);
			}

			this.fireEvent("onParameterUpdated", { name, value });
		}
	}

	setParameters(parameters: Partial<RetrieveParameters<T>>): void {
		for (let [parameter, value] of Object.entries(parameters)) {
			this.setParameter(parameter, value);
		}
	}

	private getFieldType(field: FieldDefinition, value: any): string {
		let type = field.type ?? guessType(value);
		if (!field.type && value !== null) {
			field.type = type;
		}

		return type;
	}

	canModifyCurrentRow = (): boolean => this.canModifyRow(this.currentIndex);

	canModifyRow = (index: number): boolean => {
		if (this.isNewRecord(index)) {
			return this.isInsertAllowed();
		} else {
			return this.isUpdateAllowed();
		}
	};

	isDeleteNeverAllowed = (): boolean => !this.options.allowDelete;
	isUpdateNeverAllowed = (): boolean => !this.options.allowUpdate;
	isInsertNeverAllowed = (): boolean => !this.options.allowInsert;

	/**
	 * Checks if deleting row is allowed. Deleting can either be permanently disabled
	 * in the data object options, or temporarily disabled with setAllowDelete
	 * @returns {boolean}
	 */
	isDeleteAllowed = (): boolean => this.options.allowDelete && !this.temporarilyDisallowDelete;

	/**
	 * Checks if updating row is allowed. Updating can either be permanently disabled
	 * in the data object options, or temporarily disabled with setAllowUpdate
	 * @returns {boolean}
	 */
	isUpdateAllowed = (): boolean => this.options.allowUpdate && !this.temporarilyDisallowUpdate;

	/**
	 * Checks if creating new rows is allowed. Insert can either be permanently disabled
	 * in the data object options, or temporarily disabled with setAllowInsert
	 * @returns {boolean}
	 */
	isInsertAllowed = (): boolean => this.options.allowInsert && !this.temporarilyDisallowInsert;

	setAllowDelete = (allow: boolean): void => {
		const disallow = !allow;
		if (this.temporarilyDisallowDelete !== disallow) {
			this.temporarilyDisallowDelete = disallow;
			this.fireEvent("onAllowDeleteChanged", !disallow);
		}
	};

	setAllowUpdate = (allow: boolean): void => {
		const disallow = !allow;
		if (this.temporarilyDisallowUpdate !== disallow) {
			this.temporarilyDisallowUpdate = disallow;
			this.fireEvent("onAllowUpdateChanged", !disallow);
		}
	};

	setAllowInsert = (allow: boolean): void => {
		const disallow = !allow;
		if (this.temporarilyDisallowInsert !== disallow) {
			this.temporarilyDisallowInsert = disallow;
			this.fireEvent("onAllowInsertChanged", !disallow);
		}
	};

	refreshCurrentRow = async (): Promise<T | undefined> => await this.refreshRow(this.currentIndex);

	private waitForLoadOrFail = async () => {
		let controller = new AbortController();
		let { signal } = controller;

		let load = new Promise<void>((resolve, reject) => {
			this.attachEvent("onDataLoaded", () => resolve(), { signal });
			this.attachEvent("onPartialDataLoaded", () => resolve(), { signal });
			this.attachEvent("onDataLoadFailed", (evt) => reject(evt.detail), { signal });
		});

		try {
			await load;
		} finally {
			controller.abort();
		}
	};

	refreshDataSource = async (options: RefreshDataSourceOptions<T> = {}): Promise<void> => {
		if (options.parameters) {
			this.setParameters(options.parameters);
		}

		if (this.isDirty()) {
			const shouldReload = await this.beforeReload.call(this);
			if (!shouldReload) {
				return;
			}
		}

		if (this.isDataLoading() && !this.areParametersChanged()) {
			try {
				return await this.waitForLoadOrFail();
			} catch {
				return;
			}
		}

		if (this.options.dynamicLoading === true) {
			this.retrievePartial(0, this.options.parameters.maxRecords || -1).catch(() => null);
		} else {
			this.dataHandlerRetrieve({ ...this.options.parameters }).catch(() => null);
		}

		try {
			await this.waitForLoadOrFail();
		} catch (error) {
			if (options.throw) {
				if (typeof error === "string") {
					throw Error(error);
				} else {
					throw error;
				}
			}
		}
	};

	refreshRow = async (index: number): Promise<T | undefined> => {
		// If already reloading data,
		if (this.isDataLoading()) {
			return;
		}

		const start = Date.now();
		const idField = this.options.uniqueIdField;
		const id = this.getSavedField(index, idField) as T[keyof T];
		const params: RetrieveParameters<T> & { masterChildCriteria: Partial<T> } = {
			filterString: "",
			whereClause: "",
			sortOrder: [],
			masterChildCriteria: {},
			returnTotal: false,
		};

		params.masterChildCriteria[idField] = id;

		try {
			const result = await this.dataHandler.retrieve(params);

			// If data was reloaded after refreshCurrentRow was called and completed before this callback
			// we need to look for a row having the same PrimKey/UniqueKey, and abort if not found
			if (this.dataLoaded !== null && start < this.dataLoaded) {
				// If index is more or equal to data length, or primkey is different
				if (index >= this.getDataLength() || this.storageRetrieve(index, idField) !== id) {
					let indexFromId = this.getIndexFromUniqueId(id); // Look for record's new index

					if (indexFromId === null) {
						return;
					}

					index = indexFromId;
				}

				// If index was reset / record's new index was not found
				if (index === null) {
					return;
				} // Abort, because the row is not visible
			}

			invariant(Array.isArray(result), `refreshRow: Invalid data from server. Expected Array, got ${typeof result}`);
			invariant(result.length === 1, `Invalid data from server. Expected 1 row, got ${result.length} rows.`);
			const row = result[0];

			invariant(
				row && isEqual(row[idField], id),
				`Incorrect row from server. Expected "${idField as string}" = '${id}', saw "${idField as string}" = '${
					row[idField]
				}'`,
			);

			this.storageUpdate(index, row);
			this.makeClean(index); // will also fire onRecordRefreshed

			return result[0];
		} catch (error: any) {
			if (error.name !== "AbortError") {
				this.fireEvent("onDataLoadFailed", error.message);
				throw error;
			}
		}
	};

	removeRow = (index: number) => {
		let storedRecord = this.storageRetrieve(index);
		let rowId: string | number | null = null;
		if (storedRecord !== null) {
			rowId = (storedRecord as StorageRecord<T>)[uid];
			this.storageDestroy(index); // Destroy the internal record
		}

		delete this.dirtyRecords[index]; // Remove dirty-record

		if (index === this.currentIndex) {
			// If deleting the current index
			// Decrement current index if this was the last record
			if (this.storageLength() <= index) {
				this.currentIndex--;
			}

			this.fireEvent("onRecordDeleted", index);
			this.fireEvent("onRecordDeleted2", { index, uid: rowId });
			// Notify all controls that we changed to a different record
			this.fireEvent("onCurrentIndexChanged", this.currentIndex);
		} else {
			// If we removed a record before the one we have selected decrement the current index
			if (index < this.currentIndex) {
				this.currentIndex--;
			}

			this.fireEvent("onRecordDeleted", index);
			this.fireEvent("onRecordDeleted2", { index, uid: rowId });
			// Since the current record did not actually change, we do not fire onCurrentIndexChanged
		}
	};

	retrievePartial = async (skip: number, maxRecords: number): Promise<void> => {
		const parameters = {
			...this.options.parameters,
			skip,
			maxRecords,
		};

		await this.dataHandlerRetrieve(parameters);
	};

	save = async (data: Partial<T>): Promise<T | false> => {
		for (let field in data) {
			this.currentRow(field, data[field] as T[keyof T]);
		}

		return await this.endEdit();
	};

	/**
	 * Saves the current record if it is dirty, and sets current index
	 * @param {number} index
	 * @returns {boolean}
	 */
	setCurrentIndex(index: number): boolean;
	/**
	 * Saves the current record if it is dirty, waits for the save to complete
	 * and then sets a new current index.
	 * @param {number} index
	 * @param {true} waitForSave
	 * @returns {Promise<boolean>}
	 */
	setCurrentIndex(index: number, waitForSave: true): Promise<boolean>;
	/**
	 * Saves the current record if it is dirty, and sets current index
	 * @param {number} index
	 * @returns {boolean}
	 */
	setCurrentIndex(index: number, waitForSave: false): boolean;
	/**
	 * Sets the current index to the index of the first record in the data object where predicate is true,
	 * and -1 otherwise.
	 * @param predicate find calls predicate once for each record of the data object, in ascending
	 * order, until it finds one where predicate returns true. If such a rcord is found, the current
	 * index is set to that record index. Otherwise, the current index is set to -1.
	 */
	setCurrentIndex(predicate: (value: StorageRecord<T>, index: number, data: StorageArray<T>) => unknown): boolean;
	/**
	 * Sets the current index to the index of the first record in the data object where predicate is true,
	 * and -1 otherwise. If the current record is dirty, setCurrentIndex will wait for it to save
	 * before changing the index.
	 * @param predicate find calls predicate once for each record of the data object, in ascending
	 * order, until it finds one where predicate returns true. If such a rcord is found, the current
	 * index is set to that record index. Otherwise, the current index is set to -1.
	 */
	setCurrentIndex(
		predicate: (value: StorageRecord<T>, index: number, data: StorageArray<T>) => unknown,
		waitForSave: true,
	): Promise<boolean>;
	/**
	 * Sets the current index to the index of the first record in the data object where predicate is true,
	 * and -1 otherwise.
	 * @param predicate find calls predicate once for each record of the data object, in ascending
	 * order, until it finds one where predicate returns true. If such a rcord is found, the current
	 * index is set to that record index. Otherwise, the current index is set to -1.
	 */
	setCurrentIndex(
		predicate: (value: StorageRecord<T>, index: number, data: StorageArray<T>) => unknown,
		waitForSave: false,
	): boolean;
	setCurrentIndex(
		indexOrPredicate: number | ((value: StorageRecord<T>, index: number, data: StorageArray<T>) => unknown),
		waitForSave?: boolean,
	): boolean | Promise<boolean> {
		let index: number = typeof indexOrPredicate === "function" ? this.findIndex(indexOrPredicate) : indexOrPredicate;

		this.checkIndexRange(index);

		const wasDirty = this.isDirty();
		if (wasDirty && this.currentIndex !== index) {
			const save = this.endEdit();

			if (waitForSave) {
				return save.then((result) => {
					if (!result) {
						return false;
					}

					return this.setIndexTo(index);
				});
			}
		}

		return this.setIndexTo(index);
	}

	private setIndexTo = async (index: number): Promise<boolean> => {
		this.checkIndexRange(index);

		let eventResult = this.fireEvent("onCurrentIndexChanging", index);

		if (eventResult === false) {
			return false;
		}

		this.currentIndex = index;
		this.fireEvent("onCurrentIndexChanged", this.currentIndex);

		return true;
	};

	private checkIndexRange = (index: number) => {
		invariant(index >= -1 && index < this.storageLength(), `No record found at the supplied index: ${index}`);
	};

	validateField(field: string | FieldDefinition, value: any): true | string {
		let fieldDef = typeof field === "string" ? this.getFieldDefinition(field as string) : field;
		if (!fieldDef) {
			return `Field '${field}' does not exist`;
		}

		if (value === null && !this.canSetFieldValueToNull(fieldDef)) {
			return `${fieldDef.name} cannot be empty`;
		}

		if (fieldDef.type && !fieldTypes[fieldDef.type].validate(value)) {
			return `${fieldDef.name} was of incorrect type`;
		}

		return true;
	}

	private validateSavingData(record: Partial<T>) {
		let recordFieldNames = Object.keys(record) as (keyof T)[];
		// Loop over fields and check if the passed fields and values are valid
		let result = this.fields
			.map((field) => {
				const prop = (field.alias ?? field.aliasName ?? field.name) as keyof T;
				if (field.alias || field.aliasName) {
					// Alias fields are ignored when saving
					recordFieldNames = recordFieldNames.filter((name) => name !== prop);
					return undefined;
				}
				if (Object.hasOwn(record, prop)) {
					// Remove field from list so we can later check if too many fields was sent in
					recordFieldNames = recordFieldNames.filter((name) => prop !== name);

					if (record[prop] === null && !this.canSetFieldValueToNull(field)) {
						return `${field.name} cannot be empty`;
					}

					if (field.type && !fieldTypes[field.type].validate(record[prop])) {
						return `${field.name} was of incorrect type`;
					}
				}
				return undefined;
			})
			.filter((value) => !!value);

		// If passed field is equal to a link field, ignore it
		let { linkFields } = this.options;
		if (linkFields !== null) {
			let fields = Array.isArray(linkFields) ? linkFields : (Object.keys(linkFields) as (keyof T)[]);
			recordFieldNames = recordFieldNames.filter((name) => !fields.includes(name));
		}

		// If there is still fields in recordFieldNames - show error
		if (recordFieldNames.length) {
			return result.concat(recordFieldNames.map((name) => `${name as string} is not a valid field name`)).join("\n");
		} else {
			return result.join("\n");
		}
	}

	bulkDestroy = async (primKeys: string[][]) => await this.dataHandler.bulkdestroy({ PrimKeys: primKeys });

	bulkInsert = async (fields: FieldDefinition[], data: T[], bulk: boolean, json: any) => {
		if (fields && !json) {
			for (let field of fields) {
				invariant(field.type && field.name, "Type and name needs to be present in fields collection");
			}
		}

		const obj: any = {
			Data: data,
			Fields: fields,
		};

		if (json) {
			obj.Json = json;
		}

		if (bulk) {
			obj.Bulk = "true";
		}

		const linkFields = this.getLinkFields();
		const masterDataObject = this.getMasterDataObject();

		if (masterDataObject && linkFields) {
			if (Array.isArray(linkFields)) {
				for (let field of linkFields) {
					obj[field] = masterDataObject.currentRow(field);
				}
			} else {
				for (let [childField, masterField] of Object.entries(linkFields)) {
					obj[childField] = masterDataObject.currentRow(masterField as keyof T);
				}
			}
		}

		let result = await this.dataHandler.bulkinsert(obj);

		return result;
	};
}
